import { ConnectError } from '@bufbuild/connect';
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Modal,
  notification,
  Space,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AdminPermissions, Permissions } from 'protocol/api/auth/dto_auth_pb';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { authModel } from '@/entities/auth';
import { userModel } from '@/entities/user';

import {
  accountPermissionsMessages,
  ADMIN_PERMISSION_FLAGS,
  ADMIN_PERMISSION_GROUPS,
  AdminPermissionFlag,
  adminPermissionFlagMessages,
  adminPermissionGroupMessages,
} from '../../config';

import styles from './account.module.scss';

const { Title, Text } = Typography;

type AccountProps = {
  userID: string;
};

export const Account = (props: AccountProps) => {
  const { userID } = props;

  const dispatch = useAppDispatch();

  const { formatMessage, formatDate } = useIntl();

  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const adminUser = useAppSelector(userModel.selectors.selectAdminUser);
  const canEditAdminPermissions = useAppSelector(
    authModel.selectors.selectCanEditAdminPermissions,
  );
  const canViewAdminPermissions = useAppSelector(
    authModel.selectors.selectCanViewAdminPermissions,
  );

  const currentUserLoadingState = useAppSelector(
    userModel.selectors.selectCurrentUserLoadingState,
  );

  const [targetPermissions, setTargetPermissions] =
    useState<AdminPermissions | null>(null);
  // The form must never render from a guessed all-false state: saving it
  // would revoke the target user's access, so it stays hidden until the
  // actual server state has arrived
  const [isPermissionsLoaded, setIsPermissionsLoaded] = useState(false);
  const [isPermissionsSaving, setIsPermissionsSaving] = useState(false);

  // reading a user's permissions requires AdminPermissions_CanView (CanEdit
  // implies it) — the backend denies the call otherwise
  useEffect(() => {
    setTargetPermissions(null);
    setIsPermissionsLoaded(false);

    if (!canViewAdminPermissions || !userID) {
      return undefined;
    }

    const requestController = new AbortController();

    const fetchTargetPermissions = async () => {
      try {
        const { Permissions: fetchedPermissions } = await dispatch(
          authModel.thunks.getUserPermissions({ UserID: userID }),
        ).unwrap();

        if (!requestController.signal.aborted) {
          setTargetPermissions(fetchedPermissions?.Admin ?? null);
          setIsPermissionsLoaded(true);
        }
      } catch (error) {
        if (!requestController.signal.aborted) {
          notification.error({ message: ConnectError.from(error).message });
        }
      }
    };

    fetchTargetPermissions();

    return () => {
      requestController.abort();
    };
  }, [userID, canViewAdminPermissions]);

  const handlePermissionFlagChange = (
    flag: AdminPermissionFlag,
    checked: boolean,
  ) => {
    setTargetPermissions(
      (prevPermissions) =>
        new AdminPermissions({
          ...prevPermissions,
          [flag]: checked,
        }),
    );
  };

  const savePermissions = async () => {
    setIsPermissionsSaving(true);

    const hasAnyPermissionFlag = ADMIN_PERMISSION_FLAGS.some(
      (flag) => targetPermissions?.[flag] ?? false,
    );

    try {
      const { Permissions: savedPermissions } = await dispatch(
        authModel.thunks.setUserPermissions({
          UserID: userID,
          // Admin left unset when every flag is off — this revokes admin
          // access entirely
          Permissions:
            targetPermissions && hasAnyPermissionFlag
              ? new Permissions({ Admin: targetPermissions })
              : undefined,
        }),
      ).unwrap();

      setTargetPermissions(savedPermissions?.Admin ?? null);

      notification.success({
        message: formatMessage(accountPermissionsMessages.saveSuccess),
      });
    } catch (error) {
      notification.error({ message: ConnectError.from(error).message });
    } finally {
      setIsPermissionsSaving(false);
    }
  };

  const handleSavePermissions = () => {
    const isDroppingOwnAccess =
      userID === adminUser?.ID &&
      !(
        targetPermissions?.BasicAccess &&
        targetPermissions?.AdminPermissionsCanEdit
      );

    if (isDroppingOwnAccess) {
      Modal.confirm({
        title: formatMessage(accountPermissionsMessages.selfLockoutTitle),
        content: formatMessage(accountPermissionsMessages.selfLockoutContent),
        onOk: savePermissions,
      });
      return;
    }

    savePermissions();
  };

  return (
    <Card
      style={{ height: '100%' }}
      title={
        <Title level={3} style={{ marginBottom: 0 }}>
          <FormattedMessage id="account.title" defaultMessage="Account" />
        </Title>
      }
      bordered={false}
      loading={currentUserLoadingState !== 'succeeded'}
    >
      <Title className={styles.subTitle} level={5} style={{ marginBottom: 16 }}>
        <FormattedMessage
          id="account.subtitle.clientInfo"
          defaultMessage="Client info"
        />
      </Title>

      <Descriptions column={1} size="small">
        <Descriptions.Item
          style={{ paddingBottom: 8 }}
          label={formatMessage({
            id: 'account.email',
            defaultMessage: 'Email address',
          })}
        >
          {currentUser?.PersonalData?.Emails[0]}
        </Descriptions.Item>

        <Descriptions.Item
          label={formatMessage({
            id: 'account.firstName',
            defaultMessage: 'First name',
          })}
        >
          {currentUser?.PersonalData?.FirstName}
        </Descriptions.Item>

        <Descriptions.Item
          label={formatMessage({
            id: 'account.lastName',
            defaultMessage: 'Last name',
          })}
        >
          {currentUser?.PersonalData?.LastName}
        </Descriptions.Item>

        <Descriptions.Item
          label={formatMessage({
            id: 'account.country',
            defaultMessage: 'Country',
          })}
        >
          {currentUser?.Settings?.Country}
        </Descriptions.Item>

        <Descriptions.Item
          label={formatMessage({
            id: 'global.created',
            defaultMessage: 'Created',
          })}
        >
          {!!currentUser?.Created?.At?.seconds &&
            formatDate(
              new Date(Number(currentUser.Created.At.seconds) * 1000),
              {
                year: 'numeric',
                month: 'long',
                day: '2-digit',
              },
            )}
        </Descriptions.Item>
      </Descriptions>

      {canViewAdminPermissions && isPermissionsLoaded && (
        <>
          <Title level={5}>
            <FormattedMessage
              id="account.subtitle.access"
              defaultMessage="Access"
            />
          </Title>

          <Space direction="vertical" size={16}>
            {ADMIN_PERMISSION_GROUPS.map((group) => (
              <Space key={group.key} direction="vertical" size={4}>
                <Text strong>
                  {formatMessage(adminPermissionGroupMessages[group.key])}
                </Text>

                {group.flags.map((flag) => (
                  <Checkbox
                    key={flag}
                    checked={targetPermissions?.[flag] ?? false}
                    disabled={isPermissionsSaving || !canEditAdminPermissions}
                    onChange={(event) =>
                      handlePermissionFlagChange(flag, event.target.checked)
                    }
                  >
                    {formatMessage(adminPermissionFlagMessages[flag])}
                  </Checkbox>
                ))}
              </Space>
            ))}
          </Space>

          {canEditAdminPermissions && (
            <div className={styles.savePermissions}>
              <Button
                type="primary"
                loading={isPermissionsSaving}
                onClick={handleSavePermissions}
              >
                {formatMessage(accountPermissionsMessages.save)}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};
